const pad = (value: number): string => String(value).padStart(2, '0');

const TORONTO_TIME_ZONE = 'America/Toronto';

const torontoDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TORONTO_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const torontoDateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TORONTO_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const dateParts = (formatter: Intl.DateTimeFormat, date: Date): Record<string, number> =>
  Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, Number(part.value)])
  );

/** Date-only values are deliberately compared as Toronto calendar days. */
export const toTorontoDateValue = (date: Date): string => {
  const parts = dateParts(torontoDateFormatter, date);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
};

/**
 * Converts a date-only deadline to the final instant of that day in Toronto.
 * The publication RPC uses America/Toronto, so this must not use the browser's
 * local time zone.
 */
export const torontoEndOfDayIso = (value: string): string => {
  const [year, month, day] = value.split('-').map(Number);
  const target = Date.UTC(year, month - 1, day, 23, 59, 59, 999);
  const candidate = new Date(target);
  const parts = dateParts(torontoDateTimeFormatter, candidate);
  const displayedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    candidate.getUTCMilliseconds()
  );

  return new Date(candidate.getTime() + target - displayedAsUtc).toISOString();
};

export type JobCreationDateError =
  | 'expires_at_before_today'
  | 'expires_at_after_start_date'
  | 'selection_date_before_expires_at'
  | 'selection_date_after_start_date'
  | 'start_date_before_today'
  | 'end_date_before_start_date';

export interface PublicationDateErrors {
  expiresAt?: JobCreationDateError;
  selectionDate?: JobCreationDateError;
}

export const validatePublicationDates = (
  expiresAt: string,
  selectionDate: string,
  startDate: string,
  today = toTorontoDateValue(new Date())
): PublicationDateErrors => {
  const errors: PublicationDateErrors = {};

  if (!expiresAt || expiresAt < today) {
    errors.expiresAt = 'expires_at_before_today';
  } else if (startDate && expiresAt > startDate) {
    errors.expiresAt = 'expires_at_after_start_date';
  }

  if (selectionDate) {
    if (selectionDate <= expiresAt) {
      errors.selectionDate = 'selection_date_before_expires_at';
    } else if (startDate && selectionDate > startDate) {
      errors.selectionDate = 'selection_date_after_start_date';
    }
  }

  return errors;
};

export const validateWorkDates = (
  startDate: string,
  endDate: string | null,
  today = toTorontoDateValue(new Date())
): Record<'startDate' | 'endDate', JobCreationDateError | undefined> => ({
  startDate: !startDate || startDate < today ? 'start_date_before_today' : undefined,
  endDate: endDate && startDate && endDate < startDate ? 'end_date_before_start_date' : undefined,
});
